/**
 * Distillery list component with table/card views
 */

import { Table } from '../common';
import DistilleryCard from './DistilleryCard';
import { useMediaQuery } from '../../hooks/useMediaQuery';
import './DistilleryList.css';

/**
 * Generates column definitions with action handlers
 * @param {Function} onEdit - Edit handler
 * @param {Function} onDelete - Delete handler
 * @returns {Array} Column definitions
 */
function getColumns(onEdit, onDelete, onViewBottles) {
  return [
    { key: 'distillery_name', label: 'Name', sortable: true },
    { key: 'country', label: 'Country', sortable: true },
    { key: 'city', label: 'City', sortable: true },
    { key: 'state_region', label: 'State/Region', sortable: true },
    {
      key: 'year_founded',
      label: 'Founded',
      sortable: true,
      render: (value) => value || '-'
    },
    {
      key: 'actions',
      label: '',
      sortable: false,
      width: '120px',
      render: (value, row) => (
        <div className="table__actions" onClick={(e) => e.stopPropagation()}>
          {row.website_url && (
            <a
              className="table__icon-btn"
              href={row.website_url}
              target="_blank"
              rel="noopener noreferrer"
              title="Visit website"
              aria-label="Visit distillery website"
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <line x1="2" y1="12" x2="22" y2="12" />
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z" />
              </svg>
            </a>
          )}
          <button
            className="table__icon-btn"
            onClick={() => onViewBottles(row)}
            title="View bottles"
            aria-label="View bottles from this distillery"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M10 2h4" />
              <path d="M10 2v4l-2 2v12a2 2 0 0 0 2 2h4a2 2 0 0 0 2-2V8l-2-2V2" />
              <path d="M8 14h8" />
            </svg>
          </button>
          <button
            className="table__icon-btn"
            onClick={() => onEdit(row)}
            title="Edit"
            aria-label="Edit distillery"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
            </svg>
          </button>
          <button
            className="table__icon-btn table__icon-btn--danger"
            onClick={() => onDelete(row)}
            title="Delete"
            aria-label="Delete distillery"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
          </button>
        </div>
      )
    }
  ];
}

/**
 * List of distilleries with responsive table/card layout
 * @param {Object} props - Component props
 * @param {Array} props.distilleries - Distillery data
 * @param {string} props.sortBy - Current sort column
 * @param {string} props.sortOrder - Current sort direction
 * @param {Function} props.onSort - Sort change handler
 * @param {Function} props.onSelect - Row/card selection handler
 * @param {Function} props.onEdit - Edit handler
 * @param {Function} props.onDelete - Delete handler
 * @param {boolean} props.loading - Loading state
 * @returns {React.ReactElement} List element
 */
export default function DistilleryList({
  distilleries,
  sortBy,
  sortOrder,
  onSort,
  onSelect,
  onEdit,
  onDelete,
  onViewBottles,
  loading
}) {
  const isMobile = useMediaQuery('(max-width: 767px)');

  if (isMobile) {
    return (
      <div className="distillery-card-list">
        {loading ? (
          <div className="distillery-card-list__loading">Loading...</div>
        ) : distilleries.length === 0 ? (
          <div className="distillery-card-list__empty">No distilleries found</div>
        ) : (
          distilleries.map(distillery => (
            <DistilleryCard
              key={distillery.id}
              distillery={distillery}
              onClick={() => onSelect(distillery)}
              onEdit={() => onEdit(distillery)}
              onDelete={() => onDelete(distillery)}
              onViewBottles={() => onViewBottles(distillery)}
            />
          ))
        )}
      </div>
    );
  }

  const columns = getColumns(onEdit, onDelete, onViewBottles);

  return (
    <Table
      columns={columns}
      data={distilleries}
      sortBy={sortBy}
      sortOrder={sortOrder}
      onSort={onSort}
      onRowClick={onSelect}
      loading={loading}
      emptyMessage="No distilleries found"
    />
  );
}
